import React from 'react';
import {connect} from 'react-redux';

import Button from 'components/Button';
import {
  ResolvableNoteBlock,
  AddNoteButton,
  RequiresActionNoteForm,
  NotesFilters,
  BookingPeriod
} from 'components/Notes';

import {
  toggleAddBookingNoteForm,
  selectIsAddBookingNoteFormOpened,
  fetchBookingNotes,
  addBookingNote,
  restoreBookingNote,
  archiveBookingNote,
  editBookingNote,
  setBookingNoteForEdit,
  fetchOtherBookingNotes,
  selectOtherBookingNotes,
  selectBookingNoteForEdit,
  resolveBookingNote
} from 'ducks/notes';

import {requiresPermission, PERMISSIONS} from 'ducks/auth';

import sortDownButton from 'assets/icons/sort-down-button.svg';
import sortUpButton from 'assets/icons/sort-up-button.svg';

import {reverse} from 'utils/urls';
import {BOOKING_DETAIL_URL} from 'config/urls';

import './styles.css';

class BookingNotes extends React.Component<any> {
  state = {
    showOtherBookings: false
  };

  componentDidMount() {
    const {bookingId} = this.props;

    this.props.fetchOtherBookingNotes({bookingId, archived: false});
  }

  toggleOtherBookingNotes = () =>
    this.setState({showOtherBookings: !this.state.showOtherBookings});

  toggleAddBookingNoteForm = ({isOpened}) => {
    const {locationRef} = this.props;

    this.props.requiresPermission(
      {
        permission: PERMISSIONS.CREATE_BOOKING_NOTE,
        locationRef
      },
      () => {
        return this.props.toggleAddBookingNoteForm({isOpened: isOpened});
      }
    );
  };

  filterActiveNotes = () => {
    const {bookingId} = this.props;
    this.props.fetchBookingNotes({bookingId, archived: false});
    this.props.fetchOtherBookingNotes({bookingId, archived: false});
  };

  filterArchivedNotes = () => {
    const {bookingId} = this.props;
    this.props.fetchBookingNotes({bookingId, archived: true});
    this.props.fetchOtherBookingNotes({bookingId, archived: true});
  };

  submitAddBookingNoteForm = ({text, requiresAction}) => {
    const {bookingId} = this.props;
    this.props.addBookingNote({bookingId, text, requiresAction}, [
      () => toggleAddBookingNoteForm({isOpened: false}),
      () => fetchBookingNotes({bookingId, archived: false})
    ]);
  };

  restoreNote = ({noteId}) => {
    const {bookingId, locationRef} = this.props;

    const extraActions = [() => fetchBookingNotes({bookingId, archived: true})];

    this.props.requiresPermission(
      {
        permission: PERMISSIONS.EDIT_BOOKING_NOTE,
        locationRef
      },
      () => {
        this.props.restoreBookingNote({note: noteId}, extraActions);
      }
    );
  };

  archiveNote = ({noteId}) => {
    const {bookingId, locationRef} = this.props;

    const extraActions = [
      () => fetchBookingNotes({bookingId, archived: false})
    ];

    this.props.requiresPermission(
      {
        permission: PERMISSIONS.EDIT_BOOKING_NOTE,
        locationRef
      },
      () => {
        this.props.archiveBookingNote({note: noteId}, extraActions);
      }
    );
  };

  submitEditNoteForm = ({text, noteId, requiresAction}) => {
    const {bookingId} = this.props;

    this.props.editBookingNote({text, note: noteId, requiresAction}, [
      () => setBookingNoteForEdit({noteId: null}),
      () => fetchBookingNotes({bookingId, archived: false})
    ]);
  };

  setNoteForEdit = ({noteId}) => {
    this.props.requiresPermission(
      {
        permission: PERMISSIONS.EDIT_BOOKING_NOTE,
        locationRef: this.props.locationRef
      },
      () => {
        this.props.setBookingNoteForEdit({noteId});
      }
    );
  };

  resolveNote = ({noteId}) => {
    const {bookingId} = this.props;

    const extraActions = [
      () => fetchOtherBookingNotes({bookingId, archived: false}),
      () => fetchBookingNotes({bookingId, archived: false})
    ];

    this.props.requiresPermission(
      {
        permission: PERMISSIONS.RESOLVE_BOOKING_NOTE,
        locationRef: this.props.locationRef
      },
      () => {
        this.props.resolveBookingNote({noteId}, extraActions);
      }
    );
  };

  redirectToBookingDetail = ({bookingId}) => {
    const {locationRef} = this.props;

    this.props.requiresPermission(
      {permission: PERMISSIONS.VIEW_BOOKING, locationRef},
      () => {
        window.open(reverse(BOOKING_DETAIL_URL, {bookingId, locationRef}));
      }
    );
  };

  render() {
    const {bookingNotes, isAddNoteFormOpened, otherBookingNotes} = this.props;

    const {showOtherBookings} = this.state;

    return (
      <div className="BookingNotes">
        {!isAddNoteFormOpened && (
          <div className="actions">
            <AddNoteButton
              openAddNoteForm={() =>
                this.toggleAddBookingNoteForm({isOpened: true})
              }
            />
            <NotesFilters
              filterActiveNotes={this.filterActiveNotes}
              filterArchivedNotes={this.filterArchivedNotes}
            />
          </div>
        )}
        {isAddNoteFormOpened && (
          <RequiresActionNoteForm
            closeForm={() => this.toggleAddBookingNoteForm({isOpened: false})}
            submitAction={this.submitAddBookingNoteForm}
            formName="addBookingNote"
          />
        )}
        <div className="notes">
          {bookingNotes.map(note => (
            <ResolvableNoteBlock
              key={note.id}
              note={note}
              archiveNote={this.archiveNote}
              restoreNote={this.restoreNote}
              resolveNote={this.resolveNote}
              submitEditNoteForm={this.submitEditNoteForm}
              setNoteForEdit={this.setNoteForEdit}
              addOptions={true}
              editNote={true}
              editNoteSelector={selectBookingNoteForEdit}
              optionsDropdownClassName="BookingNotesDropdown"
              editFormName="editBookingNote"
            />
          ))}
        </div>
        <div className="showOtherBookingsButtonContainer">
          <Button
            white
            className="showOtherBookingsButton"
            onClick={this.toggleOtherBookingNotes}>
            <div>Other booking notes</div>
            <img
              src={showOtherBookings ? sortUpButton : sortDownButton}
              alt="other bookings"
            />
          </Button>
        </div>

        <div className="otherBookingNotes">
          {showOtherBookings &&
            otherBookingNotes.map(booking => (
              <div key={booking.id} className="notesPerBooking">
                <BookingPeriod
                  start={booking.periodStart}
                  end={booking.periodEnd}
                  bookingId={booking.id}
                  redirectToBookingDetail={this.redirectToBookingDetail}
                />

                {booking.notes.map(note => (
                  <ResolvableNoteBlock
                    key={note.id}
                    note={note}
                    addOptions={false}
                    resolveNote={this.resolveNote}
                  />
                ))}
              </div>
            ))}
        </div>
      </div>
    );
  }
}

const mapStateToProps = state => {
  const isAddNoteFormOpened = selectIsAddBookingNoteFormOpened(state);
  const otherBookingNotes = selectOtherBookingNotes(state);

  return {
    isAddNoteFormOpened,
    otherBookingNotes
  };
};

const mapDispatchToProps = {
  requiresPermission,
  toggleAddBookingNoteForm,
  fetchBookingNotes,
  addBookingNote,
  editBookingNote,
  archiveBookingNote,
  restoreBookingNote,
  setBookingNoteForEdit,
  fetchOtherBookingNotes,
  resolveBookingNote
};

export default connect(mapStateToProps, mapDispatchToProps)(BookingNotes);
